"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useTaskProgress = void 0;
const react_1 = require("react");
const react_2 = require("@apollo/client/react");
const use_task_progress_query_1 = require("../gql/use-task-progress.query");
const client_utils_1 = require("../../../../client/client.utils");
const store_1 = require("../../../../../store");
// Debounce window before reconciling the counter with the server after a review change.
const PROGRESS_REFRESH_DEBOUNCE_MS = 1000;
const buildTaskKey = (nodeTitle, attributeName) => `${nodeTitle} - ${attributeName}`;
const useTaskProgress = ({ nodeTitle, attributeName }) => {
    var _a, _b, _c, _d, _e, _f;
    const catalogId = (0, store_1.useSelectedCatalogId)();
    const productTreeId = (0, store_1.useProductTreeId)();
    const client = (0, react_2.useApolloClient)();
    const shouldSkip = !catalogId || !productTreeId || !nodeTitle || !attributeName;
    const taskKey = buildTaskKey(nodeTitle, attributeName);
    const variables = (0, react_1.useMemo)(() => ({
        catalogId,
        productTreeId,
        filter: {
            task: { equals: taskKey },
        },
    }), [catalogId, productTreeId, taskKey]);
    // Always read fresh values from the network — the server is the single source of truth.
    const { data, networkStatus, refetch } = (0, react_2.useQuery)(use_task_progress_query_1.GET_TASK_PROGRESS, {
        variables,
        skip: shouldSkip,
        fetchPolicy: 'no-cache',
        notifyOnNetworkStatusChange: true,
    });
    const isQueryLoading = (0, client_utils_1.isQueryLoadingOrRefetching)(networkStatus);
    const node = (_d = (_c = (_b = (_a = data === null || data === void 0 ? void 0 : data.catalog) === null || _a === void 0 ? void 0 : _a.priorityTasks) === null || _b === void 0 ? void 0 : _b.edges) === null || _c === void 0 ? void 0 : _c[0]) === null || _d === void 0 ? void 0 : _d.node;
    const hasData = Boolean(node);
    // Debounced reconciliation. A review change puts the counter into a loading state and
    // (re)starts a timer; rapid successive changes keep resetting it, so we only hit the
    // server once the user pauses. We await the in-flight mutations before refetching so the
    // server has committed every change — no optimistic math, the refetch returns the truth.
    const [isRefreshing, setIsRefreshing] = (0, react_1.useState)(false);
    const refreshTimerRef = (0, react_1.useRef)(null);
    const pendingMutationsRef = (0, react_1.useRef)(new Set());
    const refreshTokenRef = (0, react_1.useRef)(0);
    const scheduleProgressRefresh = (0, react_1.useCallback)((mutation) => {
        if (mutation !== undefined) {
            pendingMutationsRef.current.add(mutation);
        }
        refreshTokenRef.current += 1;
        const token = refreshTokenRef.current;
        setIsRefreshing(true);
        if (refreshTimerRef.current) {
            clearTimeout(refreshTimerRef.current);
        }
        refreshTimerRef.current = setTimeout(() => {
            refreshTimerRef.current = null;
            const pending = [...pendingMutationsRef.current];
            pendingMutationsRef.current.clear();
            void Promise.allSettled(pending.map((m) => m.mutation))
                .then((results) => {
                // Evict products that had failed mutations so they re-fetch on next access.
                const evicted = [];
                results.forEach((result, index) => {
                    var _a;
                    if (result.status === 'rejected') {
                        const productId = (_a = pending[index]) === null || _a === void 0 ? void 0 : _a.productId;
                        if (productId) {
                            const cacheId = client.cache.identify({ __typename: 'Product', id: productId });
                            if (cacheId) {
                                client.cache.evict({ id: cacheId });
                                evicted.push(productId);
                            }
                        }
                    }
                });
                if (evicted.length > 0) {
                    // Products with failed mutations are now evicted and will re-fetch on next access.
                }
                return refetch();
            })
                .finally(() => {
                // Only the most recent schedule clears the loading state, so a click that
                // lands while this refetch is in flight keeps the counter in its loading state.
                if (refreshTokenRef.current === token) {
                    setIsRefreshing(false);
                }
            });
        }, PROGRESS_REFRESH_DEBOUNCE_MS);
    }, [refetch, client.cache]);
    (0, react_1.useEffect)(() => () => {
        if (refreshTimerRef.current) {
            clearTimeout(refreshTimerRef.current);
        }
    }, []);
    return {
        productsReviewed: (_e = node === null || node === void 0 ? void 0 : node.productsReviewed) !== null && _e !== void 0 ? _e : 0,
        taskCount: (_f = node === null || node === void 0 ? void 0 : node.taskCount) !== null && _f !== void 0 ? _f : undefined,
        scheduleProgressRefresh,
        refetchTaskProgress: refetch,
        isLoading: isQueryLoading || isRefreshing || (!shouldSkip && !hasData),
    };
};
exports.useTaskProgress = useTaskProgress;
